import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation, useSearch } from "wouter";
import { useGoBack } from "@/hooks/useGoBack";
import { CalendarDays, Clock, MapPin, Users, X, Check, Loader2, Trophy, ChevronRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { ClubPicker } from "@/components/ClubPicker";

interface Friend {
  id: string;
  name: string;
  email: string | null;
  skillLevel: number | null;
  isLeagueMember?: boolean;
}

interface League {
  id: string;
  name: string;
  imageUrl?: string | null;
  homeClubId?: string | null;
}

interface LeagueMember {
  id: string;
  userId: string;
  userName: string;
  userImage?: string | null;
  rating: number;
  role?: string;
}

interface LeagueDetails {
  league: League;
  members: LeagueMember[];
}

export function ScheduleMatchPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const goBack = useGoBack("/");
  
  // Get fixed league from URL params (admin mode)
  const searchString = useSearch();
  const fixedLeagueId = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return params.get("leagueId") || null;
  }, [searchString]);
  
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [location, setLocation_] = useState("");
  const [court, setCourt] = useState("");
  const [homeClubId, setHomeClubId] = useState<string | null>(null);
  const [clubTouched, setClubTouched] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  
  const [team1Player1, setTeam1Player1] = useState<Friend | null>(null);
  const [partner, setPartner] = useState<Friend | null>(null);
  const [opponent1, setOpponent1] = useState<Friend | null>(null);
  const [opponent2, setOpponent2] = useState<Friend | null>(null);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectingFor, setSelectingFor] = useState<"team1Player1" | "partner" | "opponent1" | "opponent2" | null>(null);

  const { data: friends = [] } = useQuery<Friend[]>({
    queryKey: ["/api/friends"],
  });

  const { data: leagues = [] } = useQuery<League[]>({
    queryKey: ["/api/leagues"],
  });

  // For fixed league mode, always use fixed league details
  const effectiveLeagueId = fixedLeagueId || selectedLeagueId;

  const { data: leagueDetails } = useQuery<LeagueDetails>({
    queryKey: ["/api/leagues", effectiveLeagueId],
    queryFn: async () => {
      if (!effectiveLeagueId) return null;
      const res = await fetch(`/api/leagues/${effectiveLeagueId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch league');
      return res.json();
    },
    enabled: !!effectiveLeagueId,
  });

  // Auto-select fixed league
  useEffect(() => {
    if (fixedLeagueId && !selectedLeagueId) {
      setSelectedLeagueId(fixedLeagueId);
    }
  }, [fixedLeagueId]);

  // Detect if user is admin of the fixed league
  const isAdminMode = useMemo(() => {
    if (!fixedLeagueId || !leagueDetails?.members || !user?.id) return false;
    const currentMember = leagueDetails.members.find((m: any) => m.userId === user.id);
    return currentMember?.role === "owner" || currentMember?.role === "admin";
  }, [fixedLeagueId, leagueDetails, user?.id]);

  const createMatchMutation = useMutation({
    mutationFn: async (data: {
      scheduledDate: string;
      scheduledTime: string;
      location: string;
      court?: string;
      leagueId?: string;
      team1Player1Id?: string;
      team1Player2Id?: string;
      team2Player1Id: string;
      team2Player2Id?: string;
      notes?: string;
      homeClubId?: string | null;
    }) => {
      return apiRequest("POST", "/api/scheduled-matches", data);
    },
    onSuccess: () => {
      toast({
        title: "Partido agendado",
        description: "El partido ha sido agendado exitosamente.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      goBack();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo agendar el partido",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    // In admin mode, team1Player1 must be selected
    if (isAdminMode && !team1Player1) {
      toast({
        title: "Campos requeridos",
        description: "Como administrador, debes seleccionar el Jugador 1 del Equipo 1.",
        variant: "destructive",
      });
      return;
    }
    
    if (!scheduledDate || !scheduledTime || !location || !opponent1) {
      toast({
        title: "Campos requeridos",
        description: "Fecha, hora, lugar y al menos un contrincante son requeridos.",
        variant: "destructive",
      });
      return;
    }

    createMatchMutation.mutate({
      scheduledDate,
      scheduledTime,
      location,
      court: court || undefined,
      leagueId: fixedLeagueId || selectedLeagueId || undefined,
      team1Player1Id: isAdminMode ? team1Player1?.id : undefined,
      team1Player2Id: partner?.id,
      team2Player1Id: opponent1.id,
      team2Player2Id: opponent2?.id,
      notes: notes || undefined,
      homeClubId,
    });
  };

  // Auto-fill homeClubId from selected league or user profile,
  // unless the user has explicitly picked/cleared a club.
  type ProfileLike = {
    country?: string | null;
    homeClubId?: string | null;
  } | null | undefined;
  const userProfile = user?.profile as ProfileLike;
  const profileHomeClubId = userProfile?.homeClubId || null;
  const profileCountry = userProfile?.country || null;
  useEffect(() => {
    if (clubTouched) return;
    const fallback =
      leagueDetails?.league?.homeClubId || profileHomeClubId || null;
    setHomeClubId(fallback);
  }, [leagueDetails, profileHomeClubId, clubTouched]);

  const availablePlayers = (() => {
    const currentUserId = user?.id;
    // In admin mode, include team1Player1 in selected IDs
    const selectedIds = [
      isAdminMode ? team1Player1?.id : null,
      partner?.id, 
      opponent1?.id, 
      opponent2?.id
    ].filter(Boolean);
    // In admin mode, don't exclude current user (they might not be playing)
    const excludedIds = new Set([...selectedIds, isAdminMode ? null : currentUserId].filter(Boolean) as string[]);
    const players: Friend[] = [];
    const addedUserIds = new Set<string>();
    
    const friendsMap = new Map<string, Friend>();
    for (const friend of friends) {
      friendsMap.set(friend.id, friend);
    }

    const leagueMemberIds = new Set<string>();
    const effectiveLeague = fixedLeagueId || selectedLeagueId;
    if (effectiveLeague && leagueDetails?.members) {
      for (const member of leagueDetails.members) {
        leagueMemberIds.add(member.userId);
      }
    }

    if (effectiveLeague && leagueDetails?.members) {
      for (const member of leagueDetails.members) {
        if (!excludedIds.has(member.userId) && !addedUserIds.has(member.userId)) {
          const friendData = friendsMap.get(member.userId);
          players.push({
            id: member.userId,
            name: friendData?.name || member.userName,
            email: friendData?.email || null,
            skillLevel: friendData?.skillLevel || null,
            isLeagueMember: true,
          });
          addedUserIds.add(member.userId);
        }
      }
    }

    // Only add friends if not in fixed league mode (fixed league shows only members)
    if (!fixedLeagueId) {
      for (const friend of friends) {
        if (!excludedIds.has(friend.id) && !addedUserIds.has(friend.id)) {
          players.push({ 
            ...friend, 
            isLeagueMember: leagueMemberIds.has(friend.id) 
          });
          addedUserIds.add(friend.id);
        }
      }
    }

    if (!searchQuery) return players;
    return players.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
  })();

  const selectPlayer = (friend: Friend) => {
    if (selectingFor === "team1Player1") {
      setTeam1Player1(friend);
      if (!partner) {
        setSelectingFor("partner");
      } else if (!opponent1) {
        setSelectingFor("opponent1");
      } else if (!opponent2) {
        setSelectingFor("opponent2");
      } else {
        setSelectingFor(null);
      }
    } else if (selectingFor === "partner") {
      setPartner(friend);
      if (!opponent1) {
        setSelectingFor("opponent1");
      } else if (!opponent2) {
        setSelectingFor("opponent2");
      } else {
        setSelectingFor(null);
      }
    } else if (selectingFor === "opponent1") {
      setOpponent1(friend);
      if (!partner) {
        setSelectingFor("partner");
      } else if (!opponent2) {
        setSelectingFor("opponent2");
      } else {
        setSelectingFor(null);
      }
    } else if (selectingFor === "opponent2") {
      setOpponent2(friend);
      if (!partner) {
        setSelectingFor("partner");
      } else if (!opponent1) {
        setSelectingFor("opponent1");
      } else {
        setSelectingFor(null);
      }
    }
    setSearchQuery("");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const toggleLeague = (leagueId: string) => {
    if (selectedLeagueId === leagueId) {
      setSelectedLeagueId(null);
    } else {
      setSelectedLeagueId(leagueId);
    }
  };

  const PlayerSlot = ({
    player,
    label,
    onSelect,
    onClear,
    required = false,
    isCurrentUser = false,
  }: {
    player: Friend | null;
    label: string;
    onSelect: () => void;
    onClear: () => void;
    required?: boolean;
    isCurrentUser?: boolean;
  }) => (
    <div 
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl border transition-all min-h-[56px]",
        isCurrentUser 
          ? "bg-primary/5 border-primary/20" 
          : player 
            ? "bg-muted/30 border-border" 
            : "border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/20 cursor-pointer active:scale-[0.98]"
      )}
      onClick={!isCurrentUser && !player ? onSelect : undefined}
    >
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className={cn(
          "text-xs font-medium",
          isCurrentUser ? "bg-primary/20 text-primary" : "bg-muted"
        )}>
          {isCurrentUser ? "TÚ" : player ? getInitials(player.name) : <Users className="h-4 w-4 text-muted-foreground" />}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-muted-foreground mb-0.5">
          {label} {required && <span className="text-destructive">*</span>}
        </p>
        <p className={cn(
          "text-sm font-medium truncate",
          !isCurrentUser && !player && "text-muted-foreground"
        )}>
          {isCurrentUser ? "Tú" : player ? player.name : "Seleccionar"}
        </p>
      </div>
      {!isCurrentUser && player && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      {!isCurrentUser && !player && (
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header 
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => goBack()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Agendar Partido</h1>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 pt-3 pb-6">
        {selectingFor ? (
          <div className="space-y-4 max-w-md">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-lg">
                Seleccionar {selectingFor === "partner" ? "pareja" : "contrincante"}
              </h2>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectingFor(null);
                  setSearchQuery("");
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Volver
              </Button>
            </div>

            <Input
              placeholder={selectedLeagueId ? "Buscar jugador..." : "Buscar amigo..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 rounded-xl text-base"
              data-testid="input-search-friend"
            />

            {selectedLeagueId && (
              <p className="text-xs text-muted-foreground">
                Mostrando miembros de la liga seleccionada
              </p>
            )}

            <div className="space-y-1.5">
              {availablePlayers.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {selectedLeagueId 
                    ? "No hay jugadores disponibles en esta liga"
                    : friends.length === 0 
                      ? "No tienes amigos agregados" 
                      : "No se encontraron jugadores"}
                </div>
              ) : (
                availablePlayers.map((player) => (
                  <button
                    key={player.id}
                    type="button"
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 active:bg-muted text-left transition-colors"
                    onClick={() => selectPlayer(player)}
                    data-testid={`button-select-player-${player.id}`}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="text-sm">
                        {getInitials(player.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{player.name}</span>
                        {player.isLeagueMember && (
                          <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                            Liga
                          </span>
                        )}
                      </div>
                      {player.skillLevel && (
                        <div className="text-xs text-muted-foreground">
                          Nivel {player.skillLevel.toFixed(1)}
                        </div>
                      )}
                    </div>
                    <div className="h-6 w-6 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-transparent" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-md space-y-6 safe-area-bottom">
            {/* When & Where Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CalendarDays className="h-4 w-4" />
                Cuándo y dónde
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="date" className="text-xs text-muted-foreground">
                  Fecha <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="h-12 rounded-xl text-base"
                  required
                  data-testid="input-scheduled-date"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="time" className="text-xs text-muted-foreground">
                  Hora <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="h-12 rounded-xl text-base"
                  required
                  data-testid="input-scheduled-time"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="location" className="text-xs text-muted-foreground">
                  Lugar <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="location"
                  placeholder="Nombre del club o lugar"
                  value={location}
                  onChange={(e) => setLocation_(e.target.value)}
                  className="h-12 rounded-xl text-base"
                  required
                  data-testid="input-location"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="court" className="text-xs text-muted-foreground">
                  Cancha (opcional)
                </Label>
                <Input
                  id="court"
                  placeholder="Ej: Cancha 1, Pista Central"
                  value={court}
                  onChange={(e) => setCourt(e.target.value)}
                  className="h-12 rounded-xl text-base"
                  data-testid="input-court"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Club (opcional)
                </Label>
                <ClubPicker
                  value={homeClubId}
                  onChange={(id) => {
                    setClubTouched(true);
                    setHomeClubId(id);
                  }}
                  country={profileCountry}
                  testId="schedule-match-club-picker"
                />
              </div>
            </div>

            {/* League Section - Fixed or selectable */}
            {fixedLeagueId && leagueDetails?.league ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Trophy className="h-4 w-4" />
                  Liga
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl border border-primary bg-primary/5">
                  <Check className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-sm font-medium truncate flex-1">
                    {leagueDetails.league.name}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground pl-1">
                  El resultado afectará el ranking de esta liga
                </p>
              </div>
            ) : leagues.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Trophy className="h-4 w-4" />
                  Liga (opcional)
                </div>
                
                <div className="space-y-2">
                  {leagues.map((league) => {
                    const isSelected = selectedLeagueId === league.id;
                    return (
                      <label
                        key={league.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all select-none active:scale-[0.98]",
                          isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/30 hover:bg-muted/30"
                        )}
                        data-testid={`league-option-${league.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleLeague(league.id)}
                          className="pointer-events-none h-5 w-5 rounded-md"
                        />
                        <span className="text-sm font-medium truncate flex-1">
                          {league.name}
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </label>
                    );
                  })}
                </div>
                {selectedLeagueId && (
                  <p className="text-xs text-muted-foreground pl-1">
                    El resultado afectará el ranking de esta liga
                  </p>
                )}
              </div>
            )}

            {/* Teams Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Users className="h-4 w-4" />
                Jugadores
              </div>

              {/* Team 1 */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-primary pl-1">{isAdminMode ? "Equipo 1" : "Tu equipo"}</p>
                <div className="grid grid-cols-2 gap-2">
                  {isAdminMode ? (
                    <PlayerSlot
                      player={team1Player1}
                      label="Jugador 1"
                      onSelect={() => setSelectingFor("team1Player1")}
                      onClear={() => setTeam1Player1(null)}
                      required
                    />
                  ) : (
                    <PlayerSlot
                      player={null}
                      label="Jugador 1"
                      onSelect={() => {}}
                      onClear={() => {}}
                      isCurrentUser={true}
                    />
                  )}
                  <PlayerSlot
                    player={partner}
                    label="Jugador 2"
                    onSelect={() => setSelectingFor("partner")}
                    onClear={() => setPartner(null)}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs font-medium text-muted-foreground">VS</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Team 2 */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground pl-1">Contrincantes</p>
                <div className="grid grid-cols-2 gap-2">
                  <PlayerSlot
                    player={opponent1}
                    label="Jugador 1"
                    onSelect={() => setSelectingFor("opponent1")}
                    onClear={() => setOpponent1(null)}
                    required
                  />
                  <PlayerSlot
                    player={opponent2}
                    label="Jugador 2"
                    onSelect={() => setSelectingFor("opponent2")}
                    onClear={() => setOpponent2(null)}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="notes" className="text-xs text-muted-foreground">
                Notas (opcional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Información adicional sobre el partido..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="resize-none rounded-xl"
                data-testid="input-notes"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12 rounded-xl text-base"
                onClick={() => goBack()}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className="flex-1 h-12 rounded-xl text-base"
                disabled={createMatchMutation.isPending}
                onClick={handleSubmit}
                data-testid="button-submit-schedule"
              >
                {createMatchMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Agendar"
                )}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
